<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MeterEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    meter: {
      type: Object,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Meter', 'Edit General Information') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_0fjd2m07y_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_d0gz4u3nm_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_lt1ch3lbq_getActionArgs = async ($event) => {
    return {
      objectType: 'meter',
      op: 'upsert',
      data: props.meter,
    };
  };

  const act_7315092382398562_closeModal_0fjd2m07y =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_0fjd2m07y_getActionArgs,
    });
  const inst_evh_7315092382398562_close_tstyr6u26 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_0fjd2m07y,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_tstyr6u26],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_v5ob6r4yx =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_v4bxtxdwz =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_v5ob6r4yx,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_v4bxtxdwz],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_h6meo80hn =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_pen33j5tz =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_h6meo80hn,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_pen33j5tz],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_d0gz4u3nm =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_d0gz4u3nm_getActionArgs,
    });
  const inst_evh_1040167445267876_success_02fzwn1v1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_d0gz4u3nm,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_crud_lt1ch3lbq = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_lt1ch3lbq_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_02fzwn1v1],
  });
  const inst_evh_1040167445267876_submit_6t9hia3h5 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_lt1ch3lbq,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_6t9hia3h5],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_6uqzxmzzu =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_6qmhqozdv =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_6uqzxmzzu,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_6qmhqozdv],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_tstyr6u26,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_v4bxtxdwz,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_pen33j5tz,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_02fzwn1v1,
    inst_evh_1040167445267876_submit_6t9hia3h5,
    save: inst_evh_1040167445267876_submit_6t9hia3h5,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_6qmhqozdv,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_tstyr6u26 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_tstyr6u26.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_v4bxtxdwz = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_v4bxtxdwz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_pen33j5tz = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_pen33j5tz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_02fzwn1v1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_02fzwn1v1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_6t9hia3h5 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_6t9hia3h5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_6qmhqozdv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_6qmhqozdv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MeterEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="meter"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <FieldsetMeterGeneralData :meter="meter" :formId="formId">
          </FieldsetMeterGeneralData>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save changes')"
          type="submit"
          :form="forms.meter"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
